import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  BookTemplate,
  Plus,
  Trash2,
  Loader2,
  Edit3,
  X,
  Image,
  Type,
  ChevronDown,
} from 'lucide-react';
import { api, type Recipe, type Model } from '../api/client';

type RecipeFormData = {
  name: string;
  description: string;
  image_model_id: string;
  text_model_id: string;
  base_prompt_template: string;
  negative_prompt: string;
  aspect_ratio: string;
};

const ASPECT_RATIOS = [
  { value: '1:1', label: '1:1 (Square)' },
  { value: '2:3', label: '2:3 (Portrait)' },
  { value: '3:2', label: '3:2 (Landscape)' },
  { value: '4:5', label: '4:5 (Portrait)' },
  { value: '5:4', label: '5:4 (Landscape)' },
  { value: '9:16', label: '9:16 (Tall)' },
  { value: '16:9', label: '16:9 (Wide)' },
];

const emptyForm: RecipeFormData = {
  name: '',
  description: '',
  image_model_id: '',
  text_model_id: '',
  base_prompt_template: '',
  negative_prompt: '',
  aspect_ratio: '2:3',
};

export default function Recipes() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [formData, setFormData] = useState<RecipeFormData>(emptyForm);

  const { data: recipesData, isLoading } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => api.recipes.list(),
  });

  const { data: modelsData } = useQuery({
    queryKey: ['models'],
    queryFn: () => api.models.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: RecipeFormData) =>
      api.recipes.create({
        name: data.name,
        description: data.description || undefined,
        image_model_id: data.image_model_id || undefined,
        text_model_id: data.text_model_id || undefined,
        base_prompt_template: data.base_prompt_template,
        negative_prompt: data.negative_prompt || undefined,
        aspect_ratio: data.aspect_ratio,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RecipeFormData> }) =>
      api.recipes.update(id, {
        name: data.name,
        description: data.description || null,
        image_model_id: data.image_model_id || null,
        text_model_id: data.text_model_id || null,
        base_prompt_template: data.base_prompt_template,
        negative_prompt: data.negative_prompt || null,
        aspect_ratio: data.aspect_ratio,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.recipes.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  const recipes = recipesData?.recipes ?? [];
  const models = modelsData?.models ?? [];
  const textModels = models.filter((m: Model) => m.type === 'text');
  const imageModels = models.filter((m: Model) => m.type === 'image');

  const openCreateModal = () => {
    setEditingRecipe(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      name: recipe.name,
      description: recipe.description ?? '',
      image_model_id: recipe.image_model_id ?? '',
      text_model_id: recipe.text_model_id ?? '',
      base_prompt_template: recipe.base_prompt_template,
      negative_prompt: recipe.negative_prompt ?? '',
      aspect_ratio: recipe.aspect_ratio,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRecipe(null);
    setFormData(emptyForm);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingRecipe) {
      updateMutation.mutate({ id: editingRecipe.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this recipe?')) {
      deleteMutation.mutate(id);
    }
  };

  const getModelName = (modelId: string | null) => {
    if (!modelId) return 'Default';
    const model = models.find((m) => m.id === modelId);
    return model?.name || modelId;
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <BookTemplate className="w-8 h-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900">Recipes</h1>
        </div>
        <button onClick={openCreateModal} className="btn btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Recipe
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Create reusable prompt templates with pre-configured models for consistent design
        generation.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : recipes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <BookTemplate className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No recipes yet</h3>
          <p className="text-gray-500 mb-4">
            Create your first recipe to save prompt templates and model configurations.
          </p>
          <button onClick={openCreateModal} className="btn btn-primary">
            Create Recipe
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.map((recipe) => (
            <div key={recipe.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-lg text-gray-900 truncate flex-1">
                  {recipe.name}
                </h3>
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={() => openEditModal(recipe)}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 transition-colors"
                    title="Edit recipe"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(recipe.id)}
                    disabled={deleteMutation.isPending}
                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete recipe"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {recipe.description && (
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">{recipe.description}</p>
              )}

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Image className="w-4 h-4 text-purple-500" />
                  <span className="text-gray-600">Image:</span>
                  <span className="font-medium text-gray-900 truncate">
                    {getModelName(recipe.image_model_id)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Type className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-600">Text:</span>
                  <span className="font-medium text-gray-900 truncate">
                    {getModelName(recipe.text_model_id)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="px-2 py-0.5 bg-gray-100 rounded">{recipe.aspect_ratio}</span>
                <span>Updated {new Date(recipe.updated_at).toLocaleDateString()}</span>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 font-mono bg-gray-50 p-2 rounded line-clamp-3">
                  {recipe.base_prompt_template}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={closeModal} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-xl font-semibold">
                  {editingRecipe ? 'Edit Recipe' : 'Create Recipe'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="label">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Minimalist Poster Style"
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this recipe's purpose..."
                    className="input"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Image Model</label>
                    <div className="relative">
                      <select
                        value={formData.image_model_id}
                        onChange={(e) =>
                          setFormData({ ...formData, image_model_id: e.target.value })
                        }
                        className="input pr-8"
                      >
                        <option value="">Default</option>
                        {imageModels.map((model: Model) => (
                          <option key={model.id} value={model.id}>
                            {model.name || model.id}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="label">Text Model</label>
                    <div className="relative">
                      <select
                        value={formData.text_model_id}
                        onChange={(e) =>
                          setFormData({ ...formData, text_model_id: e.target.value })
                        }
                        className="input pr-8"
                      >
                        <option value="">Default</option>
                        {textModels.map((model: Model) => (
                          <option key={model.id} value={model.id}>
                            {model.name || model.id}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="label">Base Prompt Template *</label>
                  <textarea
                    value={formData.base_prompt_template}
                    onChange={(e) =>
                      setFormData({ ...formData, base_prompt_template: e.target.value })
                    }
                    placeholder="Enter the base prompt template. Use {{keyword}} for niche keyword placeholder..."
                    className="input font-mono text-sm"
                    rows={4}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: Use {'{{keyword}}'} to insert the niche keyword dynamically.
                  </p>
                </div>

                <div>
                  <label className="label">Negative Prompt</label>
                  <textarea
                    value={formData.negative_prompt}
                    onChange={(e) => setFormData({ ...formData, negative_prompt: e.target.value })}
                    placeholder="Elements to avoid in generation..."
                    className="input font-mono text-sm"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="label">Aspect Ratio</label>
                  <div className="relative">
                    <select
                      value={formData.aspect_ratio}
                      onChange={(e) => setFormData({ ...formData, aspect_ratio: e.target.value })}
                      className="input pr-8"
                    >
                      {ASPECT_RATIOS.map((ratio) => (
                        <option key={ratio.value} value={ratio.value}>
                          {ratio.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {(createMutation.isError || updateMutation.isError) && (
                  <p className="text-sm text-red-600">
                    {createMutation.error instanceof Error
                      ? createMutation.error.message
                      : updateMutation.error instanceof Error
                        ? updateMutation.error.message
                        : 'Failed to save recipe'}
                  </p>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary flex-1"
                    disabled={isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                    disabled={isPending || !formData.name || !formData.base_prompt_template}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : editingRecipe ? (
                      'Update Recipe'
                    ) : (
                      'Create Recipe'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
